import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useAuthUser } from 'react-auth-kit';
import ScoreBoardTable from "./scoreboardtable";
import LogOut from "./signout";
import PlayerEntry from "./playerentry";
import Schedule from "./schedule";
import Games from "./games";
import AddGame from "./addgame";
import Register from "./registeruser";
import LiveDisplayControl from "./liveDisplayControl";
import DeletePlayerControl from "./deletePlayerControl";
import VisitStats from "./visitStats";
import './App.css';

export default function AuthGrid() {
    const [displayPage, setDisplayPage] = useState("playerentry");
    const [userRole, setUserRole] = useState('volunteer');
    const authUser = useAuthUser();
    
    useEffect(() => {
        // Get user role from auth state
        const user = authUser();
        if (user) {
            setUserRole(user.role || 'volunteer');
        }
    }, [authUser]);
    
    // Helper function to check if user has permission
    const hasPermission = (requiredRoles) => {
        return requiredRoles.includes(userRole);
    };
    
    // Helper function to get button class names with active state
    const getButtonClass = (currentPage, isGameButton = false) => {
        const baseClass = isGameButton ? 'btn-game' : 'btn-standard';
        return displayPage === currentPage ? `${baseClass} active` : baseClass;
    };

    const renderContent = () => {
        switch (displayPage) {
            case 'playerentry':
                return <PlayerEntry />;
            case 'scoreboard':
                return <ScoreBoardTable />;
            case 'schedule':
                return <Schedule />;
            case 'games':
                return <Games />;
            case 'addgame':
                if (!hasPermission(['admin'])) {
                    return (
                        <div className="error-message">
                            <h2>Access Denied</h2>
                            <p>You don't have permission to add games.</p>
                            <p>Current role: {userRole}</p>
                            <p>Required role: admin</p>
                        </div>
                    );
                }
                return <AddGame />;
            case 'logout':
                return <LogOut />;
            case 'livedisplay':
                if (!hasPermission(['admin', 'volunteer'])) {
                    return (
                        <div className="error-message">
                            <h2>Access Denied</h2>
                            <p>You don't have permission to control the live display.</p>
                            <p>Current role: {userRole}</p>
                            <p>Required role: admin or volunteer</p>
                        </div>
                    );
                }
                return <LiveDisplayControl />;
            case 'deleteplayer':
                if (!hasPermission(['admin'])) {
                    return (
                        <div className="error-message">
                            <h2>Access Denied</h2>
                            <p>You don't have permission to delete players.</p>
                            <p>Current role: {userRole}</p>
                            <p>Required role: admin</p>
                        </div>
                    );
                }
                return <DeletePlayerControl />;
            case 'visitstats':
                if (!hasPermission(['admin'])) {
                    return (
                        <div className="error-message">
                            <h2>Access Denied</h2>
                            <p>You don't have permission to view visit stats.</p>
                            <p>Current role: {userRole}</p>
                            <p>Required role: admin</p>
                        </div>
                    );
                }
                return <VisitStats />;
            case 'twoscoreboard':
               return (
                  <div className="dual-scoreboard-container">
                     <div className="scoreboard-wrapper">
                        <ScoreBoardTable />
                     </div>
                     <div className="scoreboard-wrapper">
                        <ScoreBoardTable />
                     </div>
                  </div>
    );
            case 'register':
                if (!hasPermission(['admin'])) {
                    return (
                        <div className="error-message">
                            <h2>Access Denied</h2>
                            <p>You don't have permission to register users.</p>
                            <p>Current role: {userRole}</p>
                            <p>Required role: admin</p>
                        </div>
                    );
                }
                return <Register />;
            default:
                return <PlayerEntry />;
        }
    };

    return (
        <div style={{ flexGrow: 1 }}>
            <Grid container spacing={3}>
                {/* Header */}
                <Grid item xs={12}>
                    <Paper className="header-footer-style">
                        <Typography component={'span'}>
                            <h1>High Score Challenge - Admin Panel</h1>
                            <p style={{ fontSize: '14px', margin: '5px 0' }}>
                                Logged in as: {authUser()?.username} ({userRole})
                            </p>
                        </Typography>
                    </Paper>
                </Grid>
                
                {/* Main Navigation */}
                <Grid item xs={12}>
                    <Paper className="header-footer-style">
                        <Grid container direction={"row"} justifyContent={"center"} alignItems={"center"}>
                            <Grid item xs="auto">
                                <Typography>
                                    <button 
                                        className={getButtonClass('playerentry')}
                                        onClick={() => setDisplayPage("playerentry")}
                                    >
                                        <h4>Player Entry</h4>
                                    </button>
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Typography>
                                    <button 
                                        className={getButtonClass('scoreboard', true)}
                                        onClick={() => setDisplayPage("scoreboard")}
                                    >
                                        <h4>Single Scoreboard</h4>
                                    </button>
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Typography>
                                    <button
                                        className={getButtonClass('twoscoreboard', true)}
                                        onClick={() => setDisplayPage("twoscoreboard")}
                                    >
                                        <h4>Dual Scoreboard</h4>
                                    </button>
                                </Typography>
                            </Grid>
                            {/* Opens the public live display in a new tab for quick access */}
                            {hasPermission(['admin', 'volunteer']) && (
                                <Grid item xs="auto">
                                    <Typography>
                                        <button
                                            className="btn-game"
                                            onClick={() => window.open('/display', '_blank', 'noopener,noreferrer')}
                                        >
                                            <h4>View Live Display</h4>
                                        </button>
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Main Content Area */}
                <Grid item xs={12}>
                    <Grid>
                        <Grid item xs={12}>
                            <Paper className="main-style">
                                <Typography component={'span'}>
                                    <div className="fade-in">
                                        {renderContent()}
                                    </div>
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
                
                {/* Footer Navigation */}
                <Grid item xs={12}>
                    <Paper className="header-footer-style">
                        <Grid container justifyContent="center" alignItems="center" spacing={1} direction="row">
                            <Grid item xs="auto">
                                <Typography>
                                    <button 
                                        className={getButtonClass('schedule', true)}
                                        onClick={() => setDisplayPage("schedule")}
                                    >
                                        <h4>Schedule</h4>
                                    </button>
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Typography>
                                    <button 
                                        className={getButtonClass('games')}
                                        onClick={() => setDisplayPage("games")}
                                    >
                                        <h4>Games</h4>
                                    </button>
                                </Typography>
                            </Grid>
                            {/* Only show Live Display control to admins/volunteers */}
                            {hasPermission(['admin', 'volunteer']) && (
                                <Grid item xs="auto">
                                    <Typography>
                                        <button
                                            className={getButtonClass('livedisplay')}
                                            onClick={() => setDisplayPage("livedisplay")}
                                        >
                                            <h4>Live Display</h4>
                                        </button>
                                    </Typography>
                                </Grid>
                            )}
                            {/* Only show Add Games button to admins */}
                            {hasPermission(['admin']) && (
                                <Grid item xs="auto">
                                    <Typography>
                                        <button 
                                            className={getButtonClass('addgame')}
                                            onClick={() => setDisplayPage("addgame")}
                                        >
                                            <h4>Add Games</h4>
                                        </button>
                                    </Typography>
                                </Grid>
                            )}
                           {hasPermission(['admin']) && (
                              <Grid item xs="auto">
                                <Typography>
                                    <button
                                        className={getButtonClass('register')}
                                        onClick={() => setDisplayPage('register')}
                                    >
                                        <h4>Register User</h4>
                                    </button>
                                 </Typography>
                              </Grid>
                           )}
                           {hasPermission(['admin']) && (
                              <Grid item xs="auto">
                                <Typography>
                                    <button
                                        className={getButtonClass('deleteplayer')}
                                        onClick={() => setDisplayPage('deleteplayer')}
                                    >
                                        <h4>Delete Player</h4>
                                    </button>
                                 </Typography>
                              </Grid>
                           )}
                           {hasPermission(['admin']) && (
                              <Grid item xs="auto">
                                <Typography>
                                    <button
                                        className={getButtonClass('visitstats')}
                                        onClick={() => setDisplayPage('visitstats')}
                                    >
                                        <h4>Visit Stats</h4>
                                    </button>
                                 </Typography>
                              </Grid>
                           )}
                            <Grid item xs="auto">
                                <Typography>
                                    <button
                                       className={getButtonClass('logout')}
                                       onClick={() => setDisplayPage("logout")}
                                    >
                                    <h4>Log Out</h4>
                                    </button>
                              </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid> 
        </div>
    );
}


